import axios, { CancelTokenSource } from 'axios';
import { paths } from 'config/paths';
import { useFormik } from 'formik';
import { useRouter } from 'next/router';
import Insight from 'react-linkedin-insight-tag';
import {
	SignUpFormValues,
	SignUpSchema,
	toast,
	SignUp as View,
} from 'ui-components';
import { apiInstance, tryCatch } from 'utils';

import { useSignUp } from '~auth/queries/useSignUp';

let emailValidationSource: CancelTokenSource | null = null;
let companyNameValidationSource: CancelTokenSource | null = null;

export const SignUp = () => {
	const router = useRouter();

	const signUp = useSignUp();
	const form = useFormik<SignUpFormValues>({
		initialValues: {
			email: '',
			password: '',
			companyName: '',
			name: '',
			acceptedToTermsAndPolicy: false,
			portalUrl: '',
		},
		initialStatus: {
			isValidatingEmail: false,
			isValidatingCompanyName: false,
		},
		validationSchema: SignUpSchema,
		validate: (values) => {
			const [firstName, lastName] = values.name.split(' ');
			if (!firstName || !lastName)
				return {
					name: 'Please enter your first and last name',
				};

			return {};
		},
		onSubmit: (values, helpers) => {
			toast.promise(
				async () => {
					const [error] = await tryCatch(signUp.mutateAsync(values));
					if (error) {
						helpers.setSubmitting(false);
						throw error;
					}

					const { default: Pixel } = await import('react-facebook-pixel');
					Pixel.track('StartTrial');

					Insight.track(18368692);

					router.replace(paths.signUpVerification + '?email=' + values.email);
				},
				{
					loading: 'Signing you up...',
					success: 'Success!',
					error: (e) => e.message || 'Something went wrong!',
				}
			);
		},
	});

	async function validateEmail() {
		if (!form.values.email || form.errors.email) return;

		if (emailValidationSource) emailValidationSource.cancel();
		emailValidationSource = axios.CancelToken.source();

		form.setStatus({ ...form.status, isValidatingEmail: true });
		const [error, data] = await tryCatch(
			apiInstance.get<{
				portal_url: string | null;
				status: string;
				users: Array<Record<string, any>>;
			}>(
				`/account/search?term=${encodeURIComponent(
					form.values.email
				)}&qc=email`,
				{
					cancelToken: emailValidationSource.token,
				}
			)
		);
		form.setStatus({ ...form.status, isValidatingEmail: false });

		if (error) {
			if (!axios.isCancel(error)) {
				form.setFieldError('email', 'Failed to validate email');
			}
			return;
		}
		if (data.users.length > 0)
			form.setFieldError(
				'email',
				'This email is already in use. Please use another email address.'
			);
		else form.setFieldError('email', '');
	}

	async function validateCompanyName() {
		if (!form.values.companyName || form.errors.companyName) return;

		if (companyNameValidationSource) companyNameValidationSource.cancel();
		companyNameValidationSource = axios.CancelToken.source();

		form.setStatus({ ...form.status, isValidatingCompanyName: true });
		const [error, data] = await tryCatch(
			apiInstance.get<{
				portal_url: string | null;
				status: string;
				users: Array<Record<string, any>>;
			}>(
				`/account/search?term=${encodeURIComponent(form.values.companyName)}&qc=name`
			)
		);
		form.setStatus({ ...form.status, isValidatingCompanyName: false });

		if (error) {
			if (!axios.isCancel(error)) {
				form.setFieldError('companyName', 'Failed to get valid company slug');
			}
			return;
		}

		if (!data.portal_url) {
			form.setFieldError('companyName', 'Failed to get valid company slug');
			return;
		}

		form.setFieldValue('portalUrl', data.portal_url);
		form.setFieldError('companyName', '');
	}

	return (
		<View
			form={form}
			onEmailInputBlur={validateEmail}
			onEmailInputPaste={validateEmail}
			onCompanyNameInputPaste={validateCompanyName}
			onCompanyNameInputBlur={validateCompanyName}
		/>
	);
};
